import { type JSX, useEffect } from "react";
import type { IDataSources } from "@via/utils/interfaces.tsx";
import { useForm } from "react-hook-form";
import SelectComponent from "@via/components/select/select.tsx";
import { cloudConnectionOptions } from "@via/utils/form-validators.tsx";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetSavedAmazonCredentialsList,
  useGetSavedGoogleCredentialsList,
} from "@via/pages/data/data-sources/data-source-credentials.api.tsx";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import {
  useAmazonS3FolderList,
  useGoogleCloudFolderList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import { useAmazonS3Connection } from "@via/hooks/useAmazonS3Connection.tsx";
import { useGoogleCloudConnection } from "@via/hooks/useGoogleCloudConnection.tsx";
import {
  DataSourceProvider,
  GCSConnectionCredentials,
  Permissions,
  S3ConnectionCredentials,
} from "common";
import { ROUTES } from "@via/utils/constants.tsx";
import { useCreateDataSource } from "@via/pages/data/data-connections/data-connections.api.tsx";
import { useDataSource } from "@via/hooks/useDataSource.tsx";
import {
  BUILT_IN_ACCESS_OPTION_ID,
  BUILT_IN_ACCESS_OPTION_LABEL,
} from "@via/pages/data/constants.ts";

interface CloudDataConnectorProps {
  source: IDataSources;
}

interface CloudConnectionCredentials {
  dataSourceName?: string;
  savedCredentialId: number;
  bucketName: string;
  prefix?: string;
}

const getConnectionCredentialsContext = (sourceId: string) => {
  switch (sourceId) {
    case "Amazon":
      return useAmazonS3Connection();
    case "Google":
      return useGoogleCloudConnection();
    default:
      return null;
  }
};

const getExistedConnectionCredentialsHook = (sourceId: string) => {
  switch (sourceId) {
    case "Amazon":
      return useGetSavedAmazonCredentialsList();
    case "Google":
      return useGetSavedGoogleCredentialsList();
    default:
      return null;
  }
};

const getSelectedConnectionFoldersHook = (sourceId: string) => {
  switch (sourceId) {
    case "Amazon":
      return useAmazonS3FolderList();
    case "Google":
      return useGoogleCloudFolderList();
    default:
      return null;
  }
};

const getFolderBrowserUrl = (
  sourceId: string,
  dataSourceId: string,
  datasetId?: string | null
) => {
  const baseUrl =
    sourceId === "Amazon"
      ? `/data/cloud-data-sources/amazon-s3/${dataSourceId}`
      : sourceId === "Google"
        ? `/data/cloud-data-sources/google-cloud-storage/${dataSourceId}`
        : "";
  return datasetId ? `${baseUrl}?datasetId=${datasetId}` : baseUrl;
};

const getDefaultBucketName = (sourceId: string) => {
  switch (sourceId) {
    case "Amazon":
      return "s3://";
    case "Google":
      return "gs://";
    default:
      return "";
  }
};

const getSourceInEnum = (sourceId: string) =>
  DataSourceProvider[sourceId as keyof typeof DataSourceProvider] ?? "";

export default function CloudDataConnector({
  source,
}: CloudDataConnectorProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  let setConnectionCredentials:
    | ((newState: GCSConnectionCredentials) => void)
    | ((newState: S3ConnectionCredentials) => void);
  const connectionCredentialsContext = getConnectionCredentialsContext(
    source.id
  );
  if (connectionCredentialsContext !== null) {
    setConnectionCredentials =
      connectionCredentialsContext.setConnectionCredentials;
  }

  const existedConnectionCredentialsHook = getExistedConnectionCredentialsHook(
    source.id
  );

  if (!existedConnectionCredentialsHook) {
    throw new Error(`Unsupported data source: ${source.id}`);
  }

  const { isLoading: isSavedCredentialsLoading, data: savedCredentials = [] } =
    existedConnectionCredentialsHook;
  const credentialList = [...savedCredentials];

  /**
   * IAM Role Login for Amazon S3, Google Cloud Storage
   */
  if (source.id === "Amazon" || source.id === "Google") {
    credentialList.unshift({
      name: BUILT_IN_ACCESS_OPTION_LABEL,
      id: BUILT_IN_ACCESS_OPTION_ID,
    });
  }

  const defaultBucketName = getDefaultBucketName(source.id);

  const selectedConnectionFoldersHook = getSelectedConnectionFoldersHook(
    source.id
  );

  if (!selectedConnectionFoldersHook) {
    throw new Error(`Unsupported data source: ${source.id}`);
  }

  const { mutateAsync: getFolderList, isPending: isGetFolderListLoading } =
    selectedConnectionFoldersHook;

  const {
    mutateAsync: createDataSource,
    isPending: isCreateDataSourceLoading,
  } = useCreateDataSource();

  const isLoading = isCreateDataSourceLoading || isGetFolderListLoading;

  const { setDataSourceId } = useDataSource();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CloudConnectionCredentials>({
    mode: "onChange",
    defaultValues: {
      bucketName: defaultBucketName,
      savedCredentialId: BUILT_IN_ACCESS_OPTION_ID,
    },
  });

  useEffect(() => {
    reset({
      bucketName: defaultBucketName,
      savedCredentialId: BUILT_IN_ACCESS_OPTION_ID,
    });
  }, [defaultBucketName, reset]);

  const handleCloudConnectionFormSubmit = async (
    formPayload: CloudConnectionCredentials
  ) => {
    // Derive create data source payload from the form payload
    const createDataSourcePayload = {
      name: formPayload.dataSourceName,
      path: formPayload.bucketName,
      provider: getSourceInEnum(source.id),
      credentialId: formPayload.savedCredentialId,
      permission: Permissions.UserOwned,
    };
    // No need for data source name value when getting folder list
    delete formPayload.dataSourceName;

    await getFolderList(formPayload);
    setConnectionCredentials(formPayload);

    // If getting folders is successful, save the data source for further connections
    const dataSourceResponse = await createDataSource(createDataSourcePayload);
    setDataSourceId(dataSourceResponse.id);

    const datasetId = searchParams.get("datasetId");
    navigate(
      getFolderBrowserUrl(source.id, dataSourceResponse.id || "", datasetId)
    );
  };

  const handleCancel = (): void => {
    navigate(ROUTES.DATA);
  };

  return (
    <div>
      {isSavedCredentialsLoading ? (
        <SkeletonLoader count={1} />
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit(handleCloudConnectionFormSubmit)(e);
          }}>
          <div className="max-w-[750px] space-y-6 pb-5 pt-3">
            <div className="max-w-[750px] space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Choose Credentials&nbsp;
                  <span className="text-red-400">*</span>
                </label>
                <div className="mt-2" data-testid="credentialsSelector">
                  <SelectComponent
                    control={control}
                    name="savedCredentialId"
                    rules={cloudConnectionOptions.savedCredentialId}
                    options={
                      credentialList?.map(({ name, id }) => ({
                        label: name,
                        value: id,
                      })) ?? []
                    }
                    isLoading={isSavedCredentialsLoading}
                    placeholder="Choose Credentials"
                  />
                  {errors.savedCredentialId && (
                    <span className="text-xs text-rose-500">
                      {errors.savedCredentialId.message}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                  Data Source Name&nbsp;<span className="text-red-400">*</span>
                  <InformationCircleIcon
                    id={"dataSourceNameTooltipIcon"}
                    className="mx-2 h-4"
                  />
                  <Tooltip
                    anchorSelect={"#dataSourceNameTooltipIcon"}
                    content={"Unique name for this data source"}
                  />
                </label>
                <div className="mt-2">
                  <input
                    data-testid="dataSourceName"
                    {...register(
                      "dataSourceName",
                      cloudConnectionOptions.dataSourceName
                    )}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Enter Your Data Source Name"
                  />
                  {errors.dataSourceName && (
                    <span className="text-xs text-rose-500">
                      {errors.dataSourceName.message}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                  Data Source Path&nbsp;<span className="text-red-400">*</span>
                  <InformationCircleIcon
                    id={"bucketNameTooltipIcon"}
                    className="mx-2 h-4"
                  />
                  <Tooltip
                    anchorSelect={"#bucketNameTooltipIcon"}
                    content={"The existing cloud data source path to access"}
                  />
                </label>
                <div className="mt-2">
                  <input
                    data-testid="bucketNameInput"
                    {...register(
                      "bucketName",
                      cloudConnectionOptions.bucketName
                    )}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Enter Your Data Source Path"
                  />
                  {errors.bucketName && (
                    <span className="text-xs text-rose-500">
                      {errors.bucketName.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 justify-end py-4">
              <button
                data-testid="cloudConnectorCancelButton"
                onClick={handleCancel}
                disabled={isLoading}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Cancel
              </button>
              <button
                data-testid="cloudConnectorSubmitButton"
                type="submit"
                disabled={isLoading}
                className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <Spinner loading={isLoading}>Connect</Spinner>
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
